// Money in & out — docs/MONEY_TAXES.md §4 Phase 1.
//
// Owner, 2026-09-26: "a money tab for accounting to see money coming and going
// and also how much we are paying employees".
//
//   GET /api/office/money?from=&to=   financials.read (admin, accounting)
//
// Every figure is the server's (server/utils/moneySummary.js says what each one
// IS). This screen adds nothing up: it picks the period, lays the answer out,
// and exports it. The period is company-local calendar dates, in the URL, so a
// month can be sent to the accountant as a link.
import React, { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, Notice, SectionLabel, Stat } from '../../../ui/fw';
import { useCan } from '../../../hooks/usePermission';
import { get } from '../../shared/http';
import { fmtMoney, fmtDate, fmtDateRange, fmtDateTime, todayIso } from '../shared/format';
import { downloadText, csvLine } from '../records/OfficePage';
import { PageHeader, Refused, LoadFailed, Loading } from '../shared/Page';
import './MoneyInOut.css';

export const PERIODS = [
  ['this-month', 'This month'],
  ['last-month', 'Last month'],
  ['this-quarter', 'This quarter'],
  ['last-quarter', 'Last quarter'],
  ['this-year', 'This year'],
  ['last-year', 'Last year'],
  ['custom', 'Pick dates'],
];

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m is 1-12

/**
 * The [from, to] calendar dates a named period covers, as of `today`
 * (YYYY-MM-DD). Pure, so the month and quarter edges are testable without a
 * clock: January's "last month" is the previous December, and Q1's "last
 * quarter" is the previous year's Q4.
 */
export function periodRange(key, today) {
  const [y, m] = today.split('-').map(Number);
  const month = (yy, mm) => ({ from: `${yy}-${pad(mm)}-01`, to: `${yy}-${pad(mm)}-${pad(lastDay(yy, mm))}` });
  const quarter = (yy, q) => ({ from: `${yy}-${pad(q * 3 - 2)}-01`, to: `${yy}-${pad(q * 3)}-${pad(lastDay(yy, q * 3))}` });
  const q = Math.ceil(m / 3);
  switch (key) {
    case 'last-month': return m === 1 ? month(y - 1, 12) : month(y, m - 1);
    case 'this-quarter': return quarter(y, q);
    case 'last-quarter': return q === 1 ? quarter(y - 1, 4) : quarter(y, q - 1);
    case 'this-year': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'last-year': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'this-month':
    default: return month(y, m);
  }
}

/** Every figure on the screen, as CSV rows — what the accountant is sent. */
export function moneyCsv(s) {
  const rows = [
    ['Money in & out', `${s.from} to ${s.to}`],
    [],
    ['Money in', 'Amount', 'Count'],
    ['Invoices paid in the period', s.in.paid.total, s.in.paid.count],
    ['  of which sales tax', s.in.paid.tax, ''],
    ['Part-payments with no recorded date (not in the period)', s.in.undated.amount, s.in.undated.count],
    [`Still owed on ${s.as_of}`, s.in.outstanding.balance, s.in.outstanding.count],
    ['  of which past due', s.in.outstanding.overdue, s.in.outstanding.overdue_count],
    [],
    ['Money out — payroll', 'Amount', 'Count'],
    ['Paid to workers (net)', s.out.payroll.net, s.out.payroll.count],
    ['  gross', s.out.payroll.gross, ''],
    ['  deductions', s.out.payroll.deductions, ''],
    ['  reimbursements inside gross', s.out.payroll.reimbursements, ''],
    ['Approved, not yet paid (net)', s.out.pending.net, s.out.pending.count],
    [],
    ['Money out — expenses', 'Amount', 'Count'],
    ['Expenses dated in the period', s.out.expenses.total, s.out.expenses.count],
    ['  of which sales tax on receipts', s.out.expenses.tax, ''],
    ...s.out.expenses.by_category.map((c) => [`  ${c.label}`, c.total, c.count]),
    [],
    ['In minus payroll and expenses', s.net, ''],
  ];
  if (Array.isArray(s.per_worker)) {
    rows.push([], ['Worker', 'Paystubs', 'Gross', 'Deductions', 'Net', 'Reimbursements']);
    for (const w of s.per_worker) rows.push([w.name, w.count, w.gross, w.deductions, w.net, w.reimbursements]);
  }
  // "Paid on" is the company's day (the server's paid_on), and comes first:
  // the raw UTC stamp alone put a 10:30pm Sep 30 payment in October inside a
  // file named September. The stamp stays beside it, labelled, for tracing.
  rows.push([], ['Invoice', 'Client', 'Paid on', 'Paid at (UTC)', 'Total', 'Sales tax']);
  for (const i of s.in.paid.invoices) rows.push([i.invoice_number, i.client_name, i.paid_on || '', i.paid_at, i.total, i.tax]);
  return rows.map((r) => csvLine(r)).join('\n');
}

export default function MoneyInOut() {
  const canRead = useCan('financials', 'read');
  const canExport = useCan('financials', 'export');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const today = todayIso();

  const rawPeriod = params.get('period') || 'this-month';
  const period = PERIODS.some(([k]) => k === rawPeriod) ? rawPeriod : 'this-month';
  const named = periodRange(period === 'custom' ? 'this-month' : period, today);
  const from = period === 'custom' && DAY.test(params.get('from') || '') ? params.get('from') : named.from;
  const to = period === 'custom' && DAY.test(params.get('to') || '') ? params.get('to') : named.to;
  const backwards = from > to;

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next, { replace: true });
  };
  const choosePeriod = (key) => {
    if (key === 'custom') setParam({ period: 'custom', from, to });
    else setParam({ period: key === 'this-month' ? '' : key, from: '', to: '' });
  };

  const q = useQuery({
    queryKey: ['office', 'money', from, to],
    queryFn: () => get(`/office/money?from=${from}&to=${to}`),
    enabled: canRead && !backwards,
  });
  const s = q.data;

  const workerColumns = useMemo(() => [
    { key: 'name', label: 'Worker', role: 'title' },
    { key: 'count', label: 'Paystubs', align: 'right', role: 'fact' },
    { key: 'gross', label: 'Gross', align: 'right', role: 'fact', render: (w) => fmtMoney(w.gross) },
    { key: 'deductions', label: 'Deductions', align: 'right', role: 'hidden', render: (w) => fmtMoney(w.deductions) },
    { key: 'net', label: 'Paid (net)', align: 'right', role: 'badge', render: (w) => <strong>{fmtMoney(w.net)}</strong> },
  ], []);
  const invoiceColumns = useMemo(() => [
    { key: 'invoice_number', label: 'Invoice', role: 'title', render: (i) => <strong>{i.invoice_number}</strong> },
    { key: 'client_name', label: 'Client', role: 'subtitle', render: (i) => i.client_name || '—' },
    { key: 'paid_at', label: 'Paid', role: 'fact', render: (i) => fmtDateTime(i.paid_at) },
    { key: 'tax', label: 'Sales tax', align: 'right', role: 'hidden', render: (i) => fmtMoney(i.tax) },
    { key: 'total', label: 'Total', align: 'right', role: 'badge', render: (i) => <strong>{fmtMoney(i.total)}</strong> },
  ], []);

  if (!canRead) return <Refused what="money in and out" />;

  const exportCsv = () => downloadText(moneyCsv(s), `money-${from}-to-${to}.csv`);

  return (
    <div className="of-money" data-screen="office-MoneyInOut">
      <PageHeader
        title="Money in & out"
        meta={fmtDateRange(from, to)}
        actions={canExport && s ? <FwButton variant="ghost" icon="download" onClick={exportCsv} data-action="money-export">Export CSV</FwButton> : null}
      />
      <div className="of-filters">
        <FwField label="Period" htmlFor="of-money-period">
          <FwSelect id="of-money-period" aria-label="Period" value={period} onChange={(e) => choosePeriod(e.target.value)}>
            {PERIODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </FwSelect>
        </FwField>
        {period === 'custom' ? (
          <>
            <FwField label="From" htmlFor="of-money-from">
              <FwInput id="of-money-from" type="date" value={from} onChange={(e) => setParam({ from: e.target.value })} />
            </FwField>
            <FwField label="To" htmlFor="of-money-to">
              <FwInput id="of-money-to" type="date" value={to} onChange={(e) => setParam({ to: e.target.value })} />
            </FwField>
          </>
        ) : null}
      </div>
      {backwards ? <Notice tone="warn">The start date is after the end date.</Notice> : null}
      {q.isPending && !backwards ? <Loading lines={6} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="money in and out" /> : null}

      {s ? (
        <>
          <section className="of-money__section" data-section="in">
            <SectionLabel>Money in</SectionLabel>
            <div className="of-stats__row">
              <Stat data-stat="paid-in" label="Paid in" value={fmtMoney(s.in.paid.total)} tone={s.in.paid.total > 0 ? 'ok' : 'neutral'}
                hint={`${s.in.paid.count} invoice${s.in.paid.count === 1 ? '' : 's'} paid in full — each counted whole, any deposit included, when it was paid off`} />
              <Stat data-stat="sales-tax" label="Sales tax inside" value={fmtMoney(s.in.paid.tax)}
                hint="Collected for Connecticut on invoices paid this period. Not the OS-114 figure, which goes by invoice date" />
              <Stat data-stat="still-owed" label="Still owed" value={fmtMoney(s.in.outstanding.balance)}
                tone={s.in.outstanding.overdue > 0 ? 'warn' : 'neutral'}
                hint={s.in.outstanding.overdue > 0 ? `${fmtMoney(s.in.outstanding.overdue)} past due, as of ${fmtDate(s.as_of)}` : `as of ${fmtDate(s.as_of)}`} />
            </div>
            {s.in.undated.count > 0 ? (
              <Notice tone="info" data-note="undated">
                {`${s.in.undated.count} unpaid invoice${s.in.undated.count === 1 ? ' has' : 's have'} part-payments totalling ${fmtMoney(s.in.undated.amount)}. `}
                The app does not record the date of a part-payment, so they are not counted in any period.
              </Notice>
            ) : null}
          </section>

          <section className="of-money__section" data-section="out">
            <SectionLabel>Money out — payroll</SectionLabel>
            <div className="of-stats__row">
              <Stat data-stat="paid-out" label="Paid to workers" value={fmtMoney(s.out.payroll.net)}
                hint={`${s.out.payroll.count} paystub${s.out.payroll.count === 1 ? '' : 's'}, ${s.out.payroll.workers} worker${s.out.payroll.workers === 1 ? '' : 's'} · gross ${fmtMoney(s.out.payroll.gross)}, deductions ${fmtMoney(s.out.payroll.deductions)}`} />
              <Stat data-stat="reimbursements" label="Reimbursements" value={fmtMoney(s.out.payroll.reimbursements)}
                hint="Inside the gross above, from timesheet lines" />
              <Stat data-stat="pending" label="Approved, not paid" value={fmtMoney(s.out.pending.net)}
                tone={s.out.pending.count > 0 ? 'warn' : 'neutral'}
                hint={`${s.out.pending.count} paystub${s.out.pending.count === 1 ? '' : 's'} pending`} />
            </div>
          </section>

          <section className="of-money__section" data-section="expenses">
            <SectionLabel>Money out — expenses</SectionLabel>
            <div className="of-stats__row">
              <Stat data-stat="expenses" label="Expenses" value={fmtMoney(s.out.expenses.total)}
                hint={`${s.out.expenses.count} dated in the period, sales tax included${s.out.expenses.by_category.length ? ` · most on ${s.out.expenses.by_category[0].label.toLowerCase()}` : ''}`} />
            </div>
            <p className="of-muted" data-note="expenses-link">
              <Link to={`/expenses?period=custom&from=${from}&to=${to}`}>See every expense in this period</Link>
            </p>
          </section>

          <section className="of-money__section" data-section="net">
            <SectionLabel>In minus payroll and expenses</SectionLabel>
            <div className="of-stats__row">
              <Stat data-stat="net" label="In minus payroll and expenses" value={fmtMoney(s.net)} tone={s.net < 0 ? 'stop' : 'ok'}
                hint="Before the sales tax above, which is owed to the state. Invoices count when paid off, and equipment the day it was bought, so it is not the tax return's profit" />
            </div>
          </section>

          <section className="of-money__section" data-section="workers">
            <SectionLabel>Paid to each worker</SectionLabel>
            {Array.isArray(s.per_worker) ? (
              <DataTable columns={workerColumns} rows={s.per_worker} caption="Paid to each worker"
                empty="No paystubs were marked paid in this period." />
            ) : (
              <p className="of-muted">What each worker was paid needs the permission to see sensitive worker details.</p>
            )}
          </section>

          <section className="of-money__section" data-section="invoices">
            <SectionLabel>Invoices paid</SectionLabel>
            <DataTable columns={invoiceColumns} rows={s.in.paid.invoices} caption="Invoices paid in this period"
              onRowClick={(i) => navigate(`/invoices/${i.id}`)} empty="No invoices were paid in full in this period." />
          </section>
        </>
      ) : null}
    </div>
  );
}
